import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { MiBuildHouseAlt } from "@miplatform/assets";
import {
  MiBox,
  MiBuildPageHeader,
  MiButton,
  MiIcon,
  MiPlaceholderArea,
} from "@miplatform/components";
import { BUTTON_VARIANTS, ROUNDING_BEHAVIOR } from "@miplatform/constants";
import { getUpdatedFields } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiHexagonOutline, mdiHistory, mdiPlus } from "@mdi/js";
import { Box } from "@mui/material";
import { ActivityLogDrawer } from "./components/ActivityLogDrawer";
import { DropTypeSelect } from "./components/DropTypeSelect";
import { PriceLevelForm } from "./components/PriceLevelForm";
import { PriceMatrixTable } from "./components/PriceMatrixTable";
import { PricingMatrixHeader } from "./components/PricingMatrixHeader";
import { UpdateDependentModal } from "./components/UpdateDependentModal";
import { DEFAULT_VALUES } from "./constants/constants";
import { priceLevelSchema } from "./schema/pricingMatrix.schema";
import { IPriceLevelPayload } from "./PricingMatrix.types";
import { usePricingMatrixViewModel } from "./usePricingMatrixViewModel";

const getRoundingBehavior = (roundingBehavior: unknown) => {
  const rounding = roundingBehavior?.toString();
  if (rounding === "Up") return ROUNDING_BEHAVIOR.UP;
  if (rounding === "Down") return ROUNDING_BEHAVIOR.DOWN;
  return ROUNDING_BEHAVIOR.NONE;
};

export function PricingMatrix() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const [selectedDropTypes, setSelectedDropTypes] = useState<string[]>([]);
  const [appliedDropTypes, setAppliedDropTypes] = useState<string[]>([]);
  const [openPriceLevelForm, setOpenPriceLevelForm] = useState(false);
  const [activePriceLevelIndex, setActivePriceLevelIndex] = useState<
    number | null
  >(null);
  const [isEditing, setIsEditing] = useState(false);
  const [openUpdateDependentsModal, setOpenUpdateDependentsModal] =
    useState(false);
  const [openActivityLogDrawer, setOpenActivityLogDrawer] = useState(false);

  const form = useForm<IPriceLevelPayload>({
    mode: "onChange",
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(priceLevelSchema),
  });
  const {
    reset,
    getValues,
    formState: { dirtyFields },
  } = form;
  const { classes, theme } = useStyles();

  const {
    isLoading,
    categories,
    dropTypes,
    categoryDrops,
    priceLevels,
    getCategories,
    getDropTypes,
    getCategoryDrops,
    getPriceLevels,
    handleCreateCategoryDrop,
    handleDeleteCategoryDrop,
    handleCreatePriceLevel,
    handleUpdatePriceLevel,
    handleReorderPriceLevels,
    handleDuplicatePriceLevel,
    handleDeletePriceLevel,
  } = usePricingMatrixViewModel({ reset });

  useEffect(() => {
    getCategories();
    getDropTypes();
    getCategoryDrops();
    getPriceLevels();
  }, []);

  const groupedData = useMemo(() => {
    return categories.map((category) => {
      const categoryDrop = categoryDrops.filter(
        (drop) => drop.categoryId === category.id
      );
      return {
        category,
        categoryDrop,
      };
    });
  }, [categories, categoryDrops]);

  const categoryDropRequired = groupedData.some(
    (group) => group.category.dropRequired && group.categoryDrop.length === 0
  );
  const noDropTypesAdded = groupedData.every(
    (group) => group.categoryDrop.length === 0
  );

  const handleOpenDropTypeSelect = (
    e: React.MouseEvent<HTMLElement>,
    categoryId: string,
    catDrops: { dropTypeId: string }[]
  ) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedCategoryId(categoryId);
    setSelectedDropTypes(catDrops.map((drop) => drop.dropTypeId));
    setAppliedDropTypes(catDrops.map((drop) => drop.dropTypeId));
  };

  const handleCloseDropTypeSelect = () => {
    setSelectedDropTypes(appliedDropTypes);
    setAnchorEl(null);
  };

  const handleEditDropPrices = (level: IPriceLevelPayload, idx: number) => {
    const existingDropPrices = categoryDrops.map((cd) => {
      const existingPrice = level.dropPrices?.find(
        (dp) =>
          dp.dropTypeId === cd.dropTypeId && dp.categoryId === cd.categoryId
      );
      return {
        priceLevelId: level.id,
        id: existingPrice?.id,
        price: existingPrice?.price,
        dropTypeId: cd.dropTypeId,
        categoryId: cd.categoryId,
        dropId: cd.id,
      };
    });

    const roundingBehavior = getRoundingBehavior(level.roundingBehavior);
    reset({ ...level, dropPrices: existingDropPrices, roundingBehavior });
    setIsEditing(true);
    setActivePriceLevelIndex(idx);
  };

  const handleEditPriceLevel = (level: IPriceLevelPayload) => {
    const roundingBehavior = getRoundingBehavior(level.roundingBehavior);
    reset({ ...level, roundingBehavior });
    setIsEditing(true);
    setOpenPriceLevelForm(true);
  };

  const handleClosePriceLevelForm = () => {
    setOpenPriceLevelForm(false);
    setActivePriceLevelIndex(null);
    setIsEditing(false);
    reset(DEFAULT_VALUES);
  };

  const handleSaveDropPrices = () => {
    const data = getValues();
    const hasDependent = priceLevels.some((pl) => pl.parentId === data.id);
    if (hasDependent) {
      setOpenUpdateDependentsModal(true);
    } else {
      const updatedData = getUpdatedFields(dirtyFields, data);
      handleUpdatePriceLevel({ ...updatedData, id: data.id, name: data.name });
      handleClosePriceLevelForm();
    }
  };

  const handleCancelPriceLevel = () => {
    if (isEditing) {
      getPriceLevels();
    }
    handleClosePriceLevelForm();
  };

  return (
    <>
      <MiBuildPageHeader
        title="Pricing Matrix"
        titleIcon={
          <MiIcon path={mdiHexagonOutline} color={theme.palette.primary.main} />
        }
        loading={isLoading}
        actionComponent={
          <Box className={classes.headerActions}>
            <MiButton
              title="Activity Log"
              startIcon={{ path: mdiHistory }}
              onClick={() => setOpenActivityLogDrawer(true)}
            />
            <MiButton
              title="Add Price Level"
              variant={BUTTON_VARIANTS.CONTAINED}
              startIcon={{ path: mdiPlus }}
              onClick={() => setOpenPriceLevelForm(true)}
              disabled={
                categoryDropRequired ||
                noDropTypesAdded ||
                openPriceLevelForm ||
                activePriceLevelIndex !== null
              }
            />
          </Box>
        }
      />
      <MiBox headerTitle="Set Labor & Material Pricing">
        <PricingMatrixHeader />
        <MiPlaceholderArea
          open={categories.length === 0}
          text="No Categories Created"
          helperText="Tip: Create categories first to set up the pricing matrix."
          bgColor={theme.palette.primary.light}
          iconComponent={<img src={MiBuildHouseAlt} alt="House Icon" />}
        />
        <PriceLevelForm
          open={openPriceLevelForm}
          form={form}
          isEditing={isEditing}
          priceLevels={priceLevels}
          setActivePriceLevelIndex={setActivePriceLevelIndex}
          handleClosePriceLevelForm={handleClosePriceLevelForm}
          handleCreatePriceLevel={handleCreatePriceLevel}
          handleUpdatePriceLevel={handleUpdatePriceLevel}
        />
        <PriceMatrixTable
          form={form}
          priceLevels={priceLevels}
          groupedData={groupedData}
          categoryDrops={categoryDrops}
          openPriceLevelForm={openPriceLevelForm}
          activePriceLevelIndex={activePriceLevelIndex}
          handleEditDropPrices={handleEditDropPrices}
          handleEditPriceLevel={handleEditPriceLevel}
          handleSaveDropPrices={handleSaveDropPrices}
          handleCancelPriceLevel={handleCancelPriceLevel}
          handleOpenDropTypeSelect={handleOpenDropTypeSelect}
          handleDeleteCategoryDrop={handleDeleteCategoryDrop}
          handleReorderPriceLevels={handleReorderPriceLevels}
          handleDuplicatePriceLevel={handleDuplicatePriceLevel}
          handleDeletePriceLevel={handleDeletePriceLevel}
          handleUpdatePriceLevel={handleUpdatePriceLevel}
        />
      </MiBox>
      <DropTypeSelect
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        dropTypes={dropTypes}
        appliedDropTypes={appliedDropTypes}
        selectedDropTypes={selectedDropTypes}
        selectedCategoryId={selectedCategoryId}
        setSelectedDropTypes={setSelectedDropTypes}
        handleCreateCategoryDrop={handleCreateCategoryDrop}
        handleCloseDropTypeSelect={handleCloseDropTypeSelect}
      />
      <UpdateDependentModal
        open={openUpdateDependentsModal}
        form={form}
        priceLevels={priceLevels}
        closeModal={() => setOpenUpdateDependentsModal(false)}
        handleUpdatePriceLevel={handleUpdatePriceLevel}
        handleClosePriceLevelForm={handleClosePriceLevelForm}
      />
      <ActivityLogDrawer
        open={openActivityLogDrawer}
        onClose={() => setOpenActivityLogDrawer(false)}
      />
    </>
  );
}

const useStyles = makeStyles()((theme) => ({
  headerActions: {
    display: "flex",
    gap: theme.spacing(2),
  },
}));
